import { Box, Skeleton, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import PropTypes from 'prop-types';

import DefaultContactChip from './DefaultContactChip';

const LABEL_HEIGHT = 17;
const VALUE_HEIGHT = 20;
export const LABEL_VALUE_ROW_GAP = '9px';

// A row while the patient loads: one thin line, kept at the row's height so nothing moves when
// the values arrive.
export const PatientLabelValueSkeleton = () => (
  <Box
    data-testid='patient-label-value-skeleton'
    sx={{ display: 'flex', alignItems: 'center', height: LABEL_HEIGHT + VALUE_HEIGHT }}
  >
    <Skeleton width='80%' height={24} animation='wave' />
  </Box>
);

// One label/value row of the profile header. Every row keeps its height, so an empty value shows
// a dash instead of collapsing and the card never changes size.
const PatientLabelValue = ({ label, value, isLoaded, defaultContactKind, adornment }) => {
  if (!isLoaded) {
    return <PatientLabelValueSkeleton />;
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: LABEL_HEIGHT }}>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ lineHeight: `${LABEL_HEIGHT}px` }}
        >
          {label}
        </Typography>
        {defaultContactKind && (
          <>
            <Box component='span' sx={visuallyHidden}>
              ,
            </Box>
            <DefaultContactChip contactKind={defaultContactKind} />
          </>
        )}
      </Box>

      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: VALUE_HEIGHT, minWidth: 0 }}
      >
        <Typography
          variant='body2'
          noWrap
          title={value || undefined}
          sx={{
            lineHeight: `${VALUE_HEIGHT}px`,
            color: value ? 'text.primary' : 'text.secondary',
          }}
        >
          {value || '—'}
        </Typography>
        {value && adornment}
      </Box>
    </Box>
  );
};

PatientLabelValue.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  isLoaded: PropTypes.bool.isRequired,
  defaultContactKind: PropTypes.oneOf(['email', 'phone']),
  adornment: PropTypes.node,
};

PatientLabelValue.defaultProps = {
  value: '',
  defaultContactKind: undefined,
  adornment: null,
};

export default PatientLabelValue;
